/** Supabase Auth OAuth (Google) — redirect e erros de callback. */

export const SUPABASE_AUTH_OAUTH_CALLBACK_PATH = '/auth/callback'

export type OAuthCallbackParams = {
  code: string | null
  error: string | null
  errorDescription: string | null
}

/** Origem do redirect OAuth: sempre a do browser quando disponível (PKCE por origem). */
export const resolveAuthSiteOrigin = (
  windowOrigin?: string,
  siteUrlEnvFallback?: string,
): string => {
  const origin = windowOrigin?.trim()
  if (origin) return origin.replace(/\/$/, '')
  const fromEnv = siteUrlEnvFallback?.trim()
  if (fromEnv) return fromEnv.replace(/\/$/, '')
  return ''
}

export const buildSupabaseOAuthRedirectTo = (options?: {
  origin?: string
  siteUrlEnv?: string
  callbackPath?: string
}): string => {
  const origin = resolveAuthSiteOrigin(options?.origin, options?.siteUrlEnv)
  const path = options?.callbackPath ?? SUPABASE_AUTH_OAUTH_CALLBACK_PATH
  if (!origin) {
    throw new Error('Origem indisponível para montar redirect OAuth.')
  }
  return `${origin}${path}`
}

export const readOAuthCallbackParams = (searchParams: URLSearchParams): OAuthCallbackParams => ({
  code: searchParams.get('code'),
  error: searchParams.get('error'),
  errorDescription: searchParams.get('error_description'),
})

const normalizeOAuthErrorToken = (value: string): string => value.trim().toLowerCase()

/** Mensagens amigáveis para query params do retorno OAuth (Supabase / provedor). */
export const mapOAuthCallbackErrorToMessage = (
  error: string | null | undefined,
  errorDescription?: string | null,
): string => {
  const token = error ? normalizeOAuthErrorToken(error) : ''
  const description = errorDescription?.trim()

  if (token === 'access_denied') {
    return 'Você cancelou o login com Google.'
  }

  if (
    token === 'provider_disabled' ||
    token === 'oauth_provider_disabled' ||
    (token.includes('provider') &&
      (token.includes('disabled') || token.includes('not enabled')))
  ) {
    return 'Login com Google não está disponível no momento. Tente com email e senha.'
  }

  if (description && description.length <= 240 && !/^https?:\/\//i.test(description)) {
    return description
  }

  if (token) {
    return 'Não foi possível entrar com Google. Tente novamente ou use email e senha.'
  }

  return 'Não foi possível concluir o login com Google.'
}

export const isOAuthUserCancellation = (error: string | null | undefined): boolean =>
  normalizeOAuthErrorToken(error ?? '') === 'access_denied'
